import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AgentItem, api, download, fmtCell, fmtDay, Pipeline, Report, ReportSummary, ReportTable, Run, RUN_STATUS } from '../api';
import { useSession } from '../session';
import { useMembers } from '../components/TaskList';
import { ErrorBox, fmtDate, useAsync } from '../components/ui';

const TABS: [string, string, boolean][] = [ // kulcs, felirat, időszakos-e
  ['pipeline', 'Pipeline', false], ['sales', 'Értékesítés', true], ['forecast', 'Előrejelzés', false], ['quotes', 'Ajánlatok', true],
  ['revenue', 'Számlázás', true], ['activity', 'Aktivitás', true], ['weekly', 'Heti összefoglaló', false],
];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function preset(k: string): { from: string; to: string } {
  const now = new Date(); const y = now.getFullYear(), m = now.getMonth();
  switch (k) {
    case 'last_month': return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'quarter': return { from: iso(new Date(y, m - (m % 3), 1)), to: iso(now) };
    case 'last_quarter': { const s = m - (m % 3) - 3; return { from: iso(new Date(y, s, 1)), to: iso(new Date(y, s + 3, 0)) }; }
    case 'year': return { from: `${y}-01-01`, to: iso(now) };
    case 'last_year': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default: return { from: iso(new Date(y, m, 1)), to: iso(now) };
  }
}
const PRESETS: [string, string][] = [['month', 'e hónap'], ['last_month', 'múlt hónap'], ['quarter', 'e negyedév'], ['last_quarter', 'múlt negyedév'], ['year', 'idén'], ['last_year', 'tavaly'], ['custom', 'egyéni']];

/** M8 Riportok: beépített riportok mutatókkal és táblákkal, XLSX-export, heti összefoglaló, kérdés a Vezetői elemzőnek. */
export function ReportsPage() {
  const s = useSession();
  const canAll = ['owner', 'admin', 'finance'].includes(s.role ?? '');
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter(([k]) => (k !== 'revenue' || s.role !== 'support') && (k !== 'weekly' || canAll));
  const tab = tabs.some(([k]) => k === params.get('r')) ? params.get('r')! : 'pipeline';
  const periodic = TABS.find(([k]) => k === tab)?.[2] ?? false;
  const [pr, setPr] = useState('month');
  const [range, setRange] = useState(preset('month'));
  const [owner, setOwner] = useState('');
  const [pid, setPid] = useState('');
  const [pipes, setPipes] = useState<Pipeline[]>([]);
  const [rep, setRep] = useState<Report | null>(null);
  const members = useMembers();
  const a = useAsync();
  useEffect(() => { api<{ items: Pipeline[] }>('/pipelines').then((r) => setPipes(r.items)).catch(() => setPipes([])); }, []);
  const qs = useMemo(() => new URLSearchParams({ ...(periodic ? range : {}), ...(owner ? { owner_user_id: owner } : {}), ...(tab === 'pipeline' && pid ? { pipeline_id: pid } : {}) }).toString(), [tab, range, owner, pid, periodic]);
  useEffect(() => {
    if (tab === 'weekly') return;
    setRep(null);
    a.run(async () => setRep(await api<Report>(`/reports/${tab}?${qs}`)));
  }, [tab, qs]);
  const pick = (k: string) => { setPr(k); if (k !== 'custom') setRange(preset(k)); };

  return (
    <div>
      <div className="page-head">
        <h2>Riportok</h2>
        <div className="spacer" />
        {tab !== 'weekly' && rep && <button className="secondary" onClick={() => a.run(() => download(`/reports/${tab}/xlsx?${qs}`, `riport-${tab}.xlsx`))}>Excel-export</button>}
      </div>
      <div className="filters">{tabs.map(([k, l]) => <button key={k} className={tab === k ? '' : 'secondary'} onClick={() => setParams({ r: k })}>{l}</button>)}</div>
      {tab === 'weekly' ? <Weekly /> : (
        <>
          <div className="filters">
            {periodic && <>
              <select value={pr} onChange={(e) => pick(e.target.value)} style={{ width: 'auto' }} aria-label="Időszak">{PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <input type="date" value={range.from} onChange={(e) => { setPr('custom'); setRange({ ...range, from: e.target.value }); }} style={{ width: 'auto' }} aria-label="Kezdete" />
              <span>–</span>
              <input type="date" value={range.to} onChange={(e) => { setPr('custom'); setRange({ ...range, to: e.target.value }); }} style={{ width: 'auto' }} aria-label="Vége" />
            </>}
            {tab === 'pipeline' && pipes.length > 1 && <select value={pid} onChange={(e) => setPid(e.target.value)} style={{ width: 'auto' }} aria-label="Pipeline">
              <option value="">alapértelmezett pipeline</option>{pipes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            {canAll ? <select value={owner} onChange={(e) => setOwner(e.target.value)} style={{ width: 'auto' }} aria-label="Felelős">
              <option value="">mindenki</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
              : <span className="muted small">A saját adataidat látod.</span>}
          </div>
          <ErrorBox error={a.error} />
          {!rep ? <div className="muted">Betöltés…</div> : <ReportView rep={rep} />}
        </>
      )}
      {canAll && s.on('ai.delegate') && <Ask />}
    </div>
  );
}

function ReportView({ rep }: { rep: Report }) {
  return (
    <>
      <div className="tiles">
        {rep.kpis.map((k, i) => (
          <div key={i} className="tile" title={k.hint}>
            <b>{fmtCell(k.value, k.type, k.currency)}</b>
            <span>{k.label}{k.currency && k.currency !== 'HUF' ? ` (${k.currency})` : ''}</span>
            {k.hint && <small>{k.hint}</small>}
          </div>
        ))}
      </div>
      {rep.tables.map((t) => <ReportTableView key={t.name} t={t} />)}
      {rep.notes.map((n, i) => <p key={i} className="muted small">{n}</p>)}
    </>
  );
}

function ReportTableView({ t }: { t: ReportTable }) {
  // a sáv pénznemenként a legnagyobb értékhez mér
  const max = new Map<string, number>();
  if (t.bar) for (const r of t.rows) { const c = r.currency ?? '-'; max.set(c, Math.max(max.get(c) ?? 0, Number(r[t.bar] ?? 0))); }
  const multi = new Set(t.rows.map((r) => r.currency).filter(Boolean)).size > 1;
  const link = (r: Record<string, any>, k: string, text: string) =>
    k === 'title' && r.id ? <Link to={`/ertekesites/${r.id}`}>{text}</Link> : k === 'company' && r.company_id ? <Link to={`/cegek/${r.company_id}`}>{text}</Link> : text;
  return (
    <section className="card">
      <h3>{t.title}</h3>
      {t.rows.length === 0 ? <p className="muted">Nincs adat ebben az időszakban.</p> : (
        <table className="list report-table">
          <thead><tr>{t.columns.map((c) => <th key={c.key} className={c.type && c.type !== 'text' && c.type !== 'date' ? 'num' : ''}>{c.label}</th>)}{multi && <th>Pénznem</th>}</tr></thead>
          <tbody>{t.rows.map((r, i) => (
            <tr key={i}>
              {t.columns.map((c) => {
                const text = fmtCell(r[c.key], c.type, r.currency);
                const pct = t.bar === c.key ? (Number(r[c.key] ?? 0) / (max.get(r.currency ?? '-') || 1)) * 100 : 0;
                return (
                  <td key={c.key} className={`${c.type && c.type !== 'text' && c.type !== 'date' ? 'num' : ''} ${t.bar === c.key ? 'barcell' : ''}`}>
                    {t.bar === c.key && <i className="bar" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />}
                    <span>{link(r, c.key, text)}</span>
                  </td>
                );
              })}
              {multi && <td>{r.currency}</td>}
            </tr>
          ))}</tbody>
        </table>
      )}
    </section>
  );
}

/** Heti vezetői összefoglalók: feliratkozás, előnézet, korábbiak (sablonos és AI-elemzés). */
function Weekly() {
  const s = useSession();
  const [items, setItems] = useState<ReportSummary[] | null>(null);
  const [prefs, setPrefs] = useState<{ weekly_summary: boolean } | null>(null);
  const [preview, setPreview] = useState<{ week_start: string; text: string } | null>(null);
  const a = useAsync();
  useEffect(() => {
    a.run(async () => {
      setItems((await api<{ items: ReportSummary[] }>('/reports/summaries')).items);
      setPrefs(await api('/me/prefs'));
    });
  }, []);
  const toggle = async (on: boolean) => { const r = await a.run(() => api<{ weekly_summary: boolean }>('/me/prefs', { method: 'PUT', body: { weekly_summary: on } })); if (r) setPrefs(r); };
  const weeks = [...new Set((items ?? []).map((x) => x.week_start))];
  return (
    <>
      <ErrorBox error={a.error} />
      <section className="card">
        <h3>Heti összefoglaló e-mailben</h3>
        <p className="muted small">
          Hétfő reggel a múlt hét számai: megnyert és elvesztett dealek, pipeline, ajánlatok, számlázás, aktivitás munkatársanként.
          {s.on('ai.agents') ? ' Ha a Vezetői elemző ügynök be van kapcsolva, rövid elemzést is ír mellé; ha 10 óráig nem készül el, a számok akkor is kimennek.' : ' Hétfőn 7 óra után megy ki.'}
          {' '}A saját postafiókodból kapod (<Link to="/beallitasok/postafiok">Postafiók</Link>).
        </p>
        {prefs && <label className="check"><input type="checkbox" checked={prefs.weekly_summary} onChange={(e) => toggle(e.target.checked)} /> Kérem a heti összefoglalót</label>}
        <div className="actions"><button className="secondary" onClick={() => a.run(async () => setPreview(await api('/reports/weekly-preview')))} disabled={a.busy}>Előnézet (múlt hét)</button></div>
        {preview && <pre className="summary card">{preview.text}</pre>}
      </section>
      {weeks.map((w) => (
        <section key={w} className="card">
          <h3>{fmtDay(w)} kezdetű hét</h3>
          {(items ?? []).filter((x) => x.week_start === w).sort((x) => (x.kind === 'agent' ? -1 : 1)).map((x) => (
            <Fragment key={x.id}>
              <p className="muted small">{x.kind === 'agent' ? <span className="badge badge-agent">AI-elemzés</span> : <span className="badge">számok</span>}
                {' '}{fmtDate(x.created_at)}{x.emailed_at ? ' · e-mailben kiküldve' : ''}</p>
              <pre className="summary">{x.body}</pre>
            </Fragment>
          ))}
        </section>
      ))}
      {items?.length === 0 && <p className="muted">Még nem készült heti összefoglaló – az első a következő hétfőn.</p>}
    </>
  );
}

/** Kérdés a Vezetői elemzőnek (A8) – a válasz a riportokból készül; a futások a kérdezőhöz tartoznak. */
function Ask() {
  const s = useSession();
  const [agent, setAgent] = useState<AgentItem | null | undefined>(undefined);
  const [runs, setRuns] = useState<Run[]>([]);
  const [text, setText] = useState('');
  const a = useAsync();
  const timer = useRef<number>();
  const until = useRef(0);
  const subject = `report:${s.me?.user.id}`;
  const load = async () => {
    const r = (await api<{ items: Run[] }>(`/agents/runs?subject=${encodeURIComponent(subject)}`)).items;
    setRuns(r.slice(0, 5));
    window.clearTimeout(timer.current);
    if (r.some((x) => ['queued', 'running'].includes(x.status)) || Date.now() < until.current) timer.current = window.setTimeout(load, 2500);
  };
  useEffect(() => {
    api<{ items: AgentItem[] }>('/agents').then((r) => { const x = r.items.find((i) => i.report_qa) ?? null; setAgent(x); if (x?.enabled) load(); }).catch(() => setAgent(null));
    return () => window.clearTimeout(timer.current);
  }, []);
  if (agent === undefined || agent === null) return null;
  const send = async () => {
    if (!text.trim()) return;
    if (await a.run(() => api('/agents/tasks', { method: 'POST', body: { agent_id: agent.id, instruction: text } }))) { setText(''); until.current = Date.now() + 20_000; await load(); }
  };
  return (
    <section className="card">
      <h3>Kérdezd a Vezetői elemzőt</h3>
      {!agent.enabled ? <p className="muted">A Vezetői elemző ügynök ki van kapcsolva – az <Link to="/ugynokok">Ügynökök</Link> oldalon kapcsolható be.</p> : (
        <>
          <div className="qa">
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="pl. „Mennyit nyertünk a múlt hónapban, és ki hozta a legtöbbet?” vagy „Mekkora a lejárt kintlévőség?”" />
            <button onClick={send} disabled={a.busy || !text.trim()}>Kérdés</button>
          </div>
          <ErrorBox error={a.error} />
          <ol className="timeline">
            {runs.map((r) => (
              <li key={r.id} className="agent">
                <div className="tl-head"><span className={`badge ${r.status === 'succeeded' ? 'badge-agent' : ''}`}>{RUN_STATUS[r.status] ?? r.status}</span><span className="muted">{fmtDate(r.created_at)}</span></div>
                {r.result ? <pre className="summary">{r.result}</pre> : r.plan && <ul className="plan">{r.plan.map((p, i) => <li key={i}>{p}</li>)}</ul>}
              </li>
            ))}
          </ol>
          <p className="muted small">Az ügynök csak olvas: a beépített riportokból válaszol, adatot nem módosít.</p>
        </>
      )}
    </section>
  );
}
